import React, { useMemo } from "react";
import { Badge } from "./ui/badge";
import {
  Factory, Package, ArrowRight, ShoppingCart, AlertTriangle,
  Move, MapPin, CheckCircle2, Clock, Sparkles
} from "lucide-react";
import { fmtDate, fmtDateTime, fmtNum } from "../lib/format";

/**
 * Visualise le parcours complet d'un lot :
 *  - Stats produites/vendues/perdues/restantes
 *  - Barre DLC (production → péremption)
 *  - Timeline verticale chronologique avec balance courante
 *  - Cartes des dépôts actifs
 */
export default function LotJourney({ data }) {
  const { lot_number, product, order, lots, movements, distributors } = data;

  const distMap = useMemo(
    () => (distributors || []).reduce((acc, d) => { acc[d.id] = d; return acc; }, {}),
    [distributors]
  );
  const depotName = (id) => distMap[id]?.name || (id ? "Dépôt inconnu" : "—");
  const depotCode = (id) => distMap[id]?.code || "";

  // Ligne d'OF correspondant à ce lot
  const matchingLine = (order?.lines || []).find((ln) => ln.lot_number === lot_number);
  const producedQty = matchingLine?.quantity_produced || matchingLine?.quantity_target || 0;
  const productionDate = order?.completed_at || matchingLine?.production_date || null;
  const lineDlc = matchingLine?.dlc;

  // Catégorise un mouvement
  const categorize = (m) => {
    const reason = (m.reason || "").toLowerCase();
    if (m.type === "TRANSFER") return "transfer";
    if (m.type === "IN") {
      // Les IN issus de l'auto-placement OF (1 par barquette) sont déjà résumés
      // dans l'événement "production" unique, on les masque pour éviter le bruit.
      if (reason.startsWith("production of") || reason.startsWith("production of ")) return "skip";
      return "in";
    }
    if (m.type === "OUT") {
      if (reason.startsWith("vente")) return "sale";
      if (reason.includes("dlc") || reason.includes("périmé") || reason.includes("perime")) return "expiry";
      return "out";
    }
    if (m.type === "ADJUST") return "adjust";
    return "other";
  };

  // Stats par catégorie
  const stats = useMemo(() => {
    const s = { sold: 0, expired: 0, transferred: 0, out: 0 };
    (movements || []).forEach((m) => {
      const c = categorize(m);
      if (c === "sale") s.sold += m.quantity || 0;
      else if (c === "expiry") s.expired += m.quantity || 0;
      else if (c === "transfer") s.transferred += m.quantity || 0;
      else if (c === "out") s.out += m.quantity || 0;
    });
    return s;
  }, [movements]);

  const remaining = (lots || []).reduce((acc, l) => acc + (l.quantity || 0), 0);
  const unit = product?.unit || "";

  // Timeline événements (chronologique ASC) : [production, ...movements ASC]
  const events = useMemo(() => {
    const items = [];
    if (productionDate) {
      items.push({
        kind: "production",
        date: productionDate,
        qty: producedQty,
        reference: order?.reference,
      });
    }
    const movs = [...(movements || [])]
      .filter((m) => categorize(m) !== "skip")
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    movs.forEach((m) => items.push({ kind: categorize(m), mov: m, date: m.created_at }));

    // Calcul du solde courant après chaque événement (basé sur les événements visibles)
    let balance = 0;
    return items.map((ev) => {
      if (ev.kind === "production") {
        balance += ev.qty || 0;
      } else if (ev.mov) {
        if (ev.mov.type === "IN") balance += ev.mov.quantity || 0;
        else if (ev.mov.type === "OUT") balance -= ev.mov.quantity || 0;
        else if (ev.mov.type === "ADJUST") balance += ev.mov.quantity || 0;
        // TRANSFER = neutre sur le total
      }
      return { ...ev, balanceAfter: balance };
    });
  }, [productionDate, producedQty, order, movements]);

  // Progression DLC : % écoulé entre production et DLC (clamp 0–100)
  const dlcProgress = useMemo(() => {
    if (!productionDate || !lineDlc) return null;
    const start = new Date(productionDate).getTime();
    const end = new Date(lineDlc).getTime();
    const now = Date.now();
    if (!start || !end || end <= start) return null;
    const total = end - start;
    const elapsed = now - start;
    const pct = Math.max(0, Math.min(100, (elapsed / total) * 100));
    const daysTotal = Math.round(total / 86400000);
    const daysLeft = Math.round((end - now) / 86400000);
    return { pct, daysTotal, daysLeft, expired: now > end };
  }, [productionDate, lineDlc]);

  // Distribution actuelle par dépôt
  const byDepot = useMemo(() => {
    const map = new Map();
    (lots || []).forEach((l) => {
      if ((l.quantity || 0) <= 0) return;
      const did = l.distributor_id || "_none";
      if (!map.has(did)) map.set(did, { distributor_id: l.distributor_id, qty: 0, barquettes: [] });
      const entry = map.get(did);
      entry.qty += l.quantity || 0;
      if (l.emplacement_number != null) {
        entry.barquettes.push(`E${l.emplacement_number}${l.barquette_index ? `·B${l.barquette_index}` : ""}`);
      }
    });
    return Array.from(map.values()).sort((a, b) => b.qty - a.qty);
  }, [lots]);

  return (
    <div className="space-y-6" data-testid="lot-journey">
      {/* Stats hero */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          tone="green"
          icon={<Factory className="w-4 h-4" />}
          label="Produit"
          value={fmtNum(producedQty, unit)}
          sub={productionDate ? fmtDate(productionDate) : "—"}
        />
        <StatCard
          tone="blue"
          icon={<ShoppingCart className="w-4 h-4" />}
          label="Vendu"
          value={fmtNum(stats.sold, unit)}
          sub={producedQty ? `${Math.round((stats.sold / producedQty) * 100)} %` : "—"}
        />
        <StatCard
          tone="red"
          icon={<AlertTriangle className="w-4 h-4" />}
          label="Pertes DLC"
          value={fmtNum(stats.expired, unit)}
          sub={producedQty ? `${Math.round((stats.expired / producedQty) * 100)} %` : "—"}
        />
        <StatCard
          tone="emerald"
          icon={<Package className="w-4 h-4" />}
          label="En stock"
          value={fmtNum(remaining, unit)}
          sub={byDepot.length ? `${byDepot.length} dépôt${byDepot.length > 1 ? "s" : ""}` : "Épuisé"}
        />
      </div>

      {/* DLC progress */}
      {dlcProgress && (
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-5" data-testid="dlc-progress">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-heading text-base font-medium text-[#1A1C1A] flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#1F4E3D]" /> Cycle de fraîcheur
            </h3>
            <div className="text-xs font-mono text-[#575A57]">
              {fmtDate(productionDate)} → {fmtDate(lineDlc)}
              <span className="ml-2 text-[#1A1C1A]">
                ({dlcProgress.daysTotal} j de DLC)
              </span>
            </div>
          </div>
          <div className="relative h-3 rounded-full bg-gradient-to-r from-[#A4C9A0] via-[#F0C674] to-[#C05C47] overflow-hidden">
            <div
              className="absolute top-0 bottom-0 right-0 bg-[#1A1C1A]/10 backdrop-blur-sm"
              style={{ width: `${100 - dlcProgress.pct}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-1 h-5 bg-[#1A1C1A] rounded-full"
              style={{ left: `${dlcProgress.pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs">
            <span className="text-[#575A57]">Production</span>
            <span className={`font-semibold ${dlcProgress.expired ? "text-[#C05C47]" : dlcProgress.daysLeft <= 3 ? "text-[#8C6B2F]" : "text-[#1F4E3D]"}`}>
              {dlcProgress.expired
                ? `Périmé depuis ${Math.abs(dlcProgress.daysLeft)} j`
                : `${dlcProgress.daysLeft} j restants`}
            </span>
            <span className="text-[#575A57]">DLC</span>
          </div>
        </div>
      )}

      {/* Distribution actuelle */}
      {byDepot.length > 0 && (
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
          <h3 className="font-heading text-base font-medium text-[#1A1C1A] flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4 text-[#1F4E3D]" /> Localisation actuelle
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {byDepot.map((d) => {
              const pct = remaining ? Math.round((d.qty / remaining) * 100) : 0;
              return (
                <div
                  key={d.distributor_id || "none"}
                  className="border border-[#E5E5E0] rounded-md p-3 bg-[#F9F9F6] hover:border-[#1F4E3D]/40 transition-colors"
                  data-testid={`depot-card-${d.distributor_id || "none"}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="font-medium text-sm text-[#1A1C1A] truncate">{depotName(d.distributor_id)}</div>
                    {depotCode(d.distributor_id) && (
                      <Badge variant="outline" className="font-mono text-[10px] shrink-0">{depotCode(d.distributor_id)}</Badge>
                    )}
                  </div>
                  <div className="font-heading text-xl font-medium text-[#1F4E3D]">{fmtNum(d.qty, unit)}</div>
                  <div className="h-1.5 bg-white border border-[#E5E5E0] rounded-full overflow-hidden mt-2">
                    <div className="h-full bg-[#1F4E3D]" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-[11px] text-[#575A57]">
                    <span>{pct}% du stock</span>
                    <span>{d.barquettes.length} barq.</span>
                  </div>
                  {d.barquettes.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {d.barquettes.slice(0, 6).map((b) => (
                        <span key={b} className="font-mono text-[10px] bg-white border border-[#E5E5E0] rounded px-1.5 py-0.5 text-[#3A3A3A]">{b}</span>
                      ))}
                      {d.barquettes.length > 6 && (
                        <span className="text-[10px] text-[#575A57]">+{d.barquettes.length - 6}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-heading text-base font-medium text-[#1A1C1A] flex items-center gap-2 mb-5">
          <Sparkles className="w-4 h-4 text-[#1F4E3D]" /> Parcours du lot ({events.length} étape{events.length > 1 ? "s" : ""})
        </h3>
        {events.length === 0 ? (
          <p className="text-sm text-[#A3A6A3]">Aucun événement enregistré.</p>
        ) : (
          <ol className="relative border-l-2 border-[#E5E5E0] ml-2 space-y-5" data-testid="journey-timeline">
            {events.map((ev, i) => (
              <TimelineEvent
                key={i}
                ev={ev}
                depotName={depotName}
                depotCode={depotCode}
                unit={unit}
                isLast={i === events.length - 1}
                isFirst={i === 0}
              />
            ))}
            {remaining === 0 && (
              <li className="ml-6 relative">
                <span className="absolute -left-[34px] flex items-center justify-center w-8 h-8 rounded-full bg-[#F0F0EB] border-2 border-white ring-2 ring-[#A3A6A3]">
                  <CheckCircle2 className="w-4 h-4 text-[#575A57]" />
                </span>
                <div className="text-sm font-medium text-[#575A57]">Lot épuisé</div>
                <div className="text-xs text-[#A3A6A3]">Plus aucun stock résiduel sur ce numéro de lot.</div>
              </li>
            )}
          </ol>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ tone, icon, label, value, sub }) => {
  const tones = {
    green:   { bg: "bg-[#E8EEE9]", border: "border-[#1F4E3D]/20", text: "text-[#1F4E3D]" },
    blue:    { bg: "bg-[#EEF2F7]", border: "border-[#3B5B7A]/20", text: "text-[#3B5B7A]" },
    red:     { bg: "bg-[#FCEEEA]", border: "border-[#C05C47]/20", text: "text-[#C05C47]" },
    emerald: { bg: "bg-[#FBF4E9]", border: "border-[#8C6B2F]/20", text: "text-[#8C6B2F]" },
  }[tone] || { bg: "bg-[#F9F9F6]", border: "border-[#E5E5E0]", text: "text-[#1A1C1A]" };
  return (
    <div className={`${tones.bg} border ${tones.border} rounded-lg p-3.5`}>
      <div className={`flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold ${tones.text}`}>
        {icon}{label}
      </div>
      <div className={`font-heading text-2xl font-medium mt-1 ${tones.text}`}>{value}</div>
      <div className="text-[11px] text-[#575A57] mt-0.5">{sub}</div>
    </div>
  );
};

const TimelineEvent = ({ ev, depotName, depotCode, unit, isLast, isFirst }) => {
  const config = {
    production: { icon: <Factory className="w-4 h-4" />, ring: "ring-[#1F4E3D]", bg: "bg-[#E8EEE9]", text: "text-[#1F4E3D]", label: "Production" },
    in:         { icon: <ArrowRight className="w-4 h-4 rotate-[-90deg]" />, ring: "ring-[#1F4E3D]", bg: "bg-[#E8EEE9]", text: "text-[#1F4E3D]", label: "Entrée" },
    out:        { icon: <ArrowRight className="w-4 h-4 rotate-90" />, ring: "ring-[#C05C47]", bg: "bg-[#FCEEEA]", text: "text-[#C05C47]", label: "Sortie" },
    sale:       { icon: <ShoppingCart className="w-4 h-4" />, ring: "ring-[#3B5B7A]", bg: "bg-[#EEF2F7]", text: "text-[#3B5B7A]", label: "Vente" },
    expiry:     { icon: <AlertTriangle className="w-4 h-4" />, ring: "ring-[#8C6B2F]", bg: "bg-[#FBF4E9]", text: "text-[#8C6B2F]", label: "DLC dépassée" },
    transfer:   { icon: <Move className="w-4 h-4" />, ring: "ring-[#3B5B7A]", bg: "bg-[#EEF2F7]", text: "text-[#3B5B7A]", label: "Transfert" },
    adjust:     { icon: <Sparkles className="w-4 h-4" />, ring: "ring-[#8C6B2F]", bg: "bg-[#FBF4E9]", text: "text-[#8C6B2F]", label: "Ajustement" },
    other:      { icon: <Package className="w-4 h-4" />, ring: "ring-[#A3A6A3]", bg: "bg-[#F0F0EB]", text: "text-[#575A57]", label: "Mouvement" },
  }[ev.kind] || { icon: <Package className="w-4 h-4" />, ring: "ring-[#A3A6A3]", bg: "bg-[#F0F0EB]", text: "text-[#575A57]", label: "—" };

  const m = ev.mov;
  const qtyDisplay = ev.kind === "production"
    ? `+${fmtNum(ev.qty, unit)}`
    : m
      ? `${m.type === "OUT" ? "−" : m.type === "IN" ? "+" : ""}${fmtNum(m.quantity, unit)}`
      : "";
  const qtyClass = ev.kind === "production" || ev.kind === "in"
    ? "text-[#1F4E3D]"
    : ev.kind === "sale"
      ? "text-[#3B5B7A]"
      : ev.kind === "expiry" || ev.kind === "out"
        ? "text-[#C05C47]"
        : "text-[#575A57]";

  return (
    <li className="ml-6 relative" data-testid={`event-${ev.kind}`}>
      <span className={`absolute -left-[34px] flex items-center justify-center w-8 h-8 rounded-full ${config.bg} ${config.text} border-2 border-white ring-2 ${config.ring}`}>
        {config.icon}
      </span>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={`${config.bg} ${config.text} border-current/20 text-[11px] uppercase tracking-wider font-semibold`}>
              {config.label}
            </Badge>
            {ev.kind === "production" && ev.reference && (
              <span className="text-xs font-mono text-[#1A1C1A]">OF {ev.reference}</span>
            )}
            {qtyDisplay && (
              <span className={`font-mono font-semibold text-sm ${qtyClass}`}>{qtyDisplay}</span>
            )}
          </div>
          <div className="text-sm text-[#1A1C1A] mt-1">
            {ev.kind === "transfer" ? (
              <span className="inline-flex items-center gap-1.5 flex-wrap">
                <DepotPill name={depotName(m.from_distributor_id)} code={depotCode(m.from_distributor_id)} />
                <ArrowRight className="w-3.5 h-3.5 text-[#575A57]" />
                <DepotPill name={depotName(m.to_distributor_id)} code={depotCode(m.to_distributor_id)} />
              </span>
            ) : ev.kind === "production" ? (
              <span className="text-[#575A57] text-xs">Lot créé sur la chaîne de production</span>
            ) : m ? (
              <DepotPill name={depotName(m.distributor_id)} code={depotCode(m.distributor_id)} />
            ) : null}
          </div>
          {m?.reason && ev.kind !== "transfer" && (
            <div className="text-xs text-[#575A57] mt-1 italic">{m.reason}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[11px] font-mono text-[#575A57]">{fmtDateTime(ev.date)}</div>
          <div className="mt-1 inline-flex items-center gap-1 bg-[#F9F9F6] border border-[#E5E5E0] rounded-full px-2 py-0.5 text-[11px]">
            <span className="text-[#575A57]">solde</span>
            <span className="font-mono font-semibold text-[#1A1C1A]">{fmtNum(ev.balanceAfter, unit)}</span>
          </div>
        </div>
      </div>
    </li>
  );
};

const DepotPill = ({ name, code }) => (
  <span className="inline-flex items-center gap-1.5 bg-[#F9F9F6] border border-[#E5E5E0] rounded-md px-2 py-0.5 text-xs">
    <MapPin className="w-3 h-3 text-[#575A57]" />
    <span className="font-medium text-[#1A1C1A]">{name}</span>
    {code && <span className="font-mono text-[10px] text-[#575A57]">· {code}</span>}
  </span>
);
